import "./Unlock.css";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Content,
  Header,
  Heading,
  Main,
  SubHeading,
} from "../../commons/Template/Template";
import { Trans, useTranslation } from "react-i18next";
import Pill from "../Common/Pill/Pill";
import ProgressBar from "../Common/ProgressBar/ProgressBar";
import { useEffect, useState } from "react";
import Button from "../../commons/Button/Button";
import { CheckCircleRounded, Grading, FileCopy } from "@mui/icons-material";
import routes from "../../../routes/routes";
import api from "../../../api/resources";
import { Form, FormLoader } from "../../commons/Form/Form";
import { useModalDispatch } from "../../commons/Modal/ModalContext";
import { useToastsDispatch } from "../../commons/Toasts/ToastsContext";
import { i18n } from "../../../i18n";
import { useAuth } from "../../../auth/AuthContext";
import { capitalize } from "../../../utils";
import CreditLine from "../Common/CreditLine/CreditLine";
import { useHelperDispatch } from "../../commons/Helper/HelperContext";
import { PartnersDrawer } from "../Assessment/Results/Results";

export default function Unlock() {
  // Flow
  const location = useLocation();
  let need = "";
  let results = null;
  if (location.state) {
    need = location.state.need;
    results = location.state.results;
  }
  const navigate = useNavigate();
  const goBack = () => {
    navigate(routes.Financing.assess);
  };
  useEffect(() => {
    if (!need || !results) {
      goBack();
    }
  }, []);
  const goForward = (usecase) => {
    const target = `${routes.Financing.request}/new`;
    const state = {
      need: need,
      usecase: usecase,
    };
    navigate(target, { state: state });
  };
  // End flow

  const { t } = useTranslation(["common", "financing"]);
  document.title = t("financing:unlock.title");

  const [loading, setLoading] = useState(true);
  const { activeCompany, loadingActiveCompany } = useAuth();

  const [selectedUsecase, setSelectedUsecase] = useState("");

  const [usecases, setUsecases] = useState(null);
  const dispatchModal = useModalDispatch();
  const dispatchHelper = useHelperDispatch();

  // Delete potential previous helper
  useEffect(() => {
    dispatchHelper({ type: "delete" });
  }, []);

  const initUsecases = (
    jsonUsecases = {
      usecaseA: {
        status: "unavailable",
        reasons: ["activity"],
        actions: {},
        providers: [],
        credit_line: null,
      },
      usecaseB: {
        status: "available",
        reasons: [],
        actions: {
          add_representant_email: true,
          synchronise_accounts: false,
        },
        providers: [],
        credit_line: null,
      },
      usecaseC: {
        status: "register",
        reasons: [],
        actions: {
          add_representant_email: true,
          synchronise_accounts: true,
        },
        providers: ["defacto"],
        credit_line: null,
      },
      usecaseD: {
        status: "active",
        reasons: ["activity"],
        actions: {
          add_representant_email: true,
          synchronise_accounts: true,
        },
        providers: ["defacto"],
        credit_line: {
          max_amount: 142000,
          available_amount: 20000,
        },
      },
      usecaseE: {
        status: "register",
        reasons: [],
        actions: {
          add_representant_email: true,
          synchronise_accounts: true,
        },
        providers: ["defacto"],
        credit_line: null,
      },
    },
  ) => {
    const newUsecases = Object.entries(jsonUsecases).map(
      ([usecaseID, usecaseContent]) => {
        const {
          status,
          reasons,
          providers,
          credit_line: creditLine,
        } = usecaseContent;

        let actions = [];

        // ----- Actions -----
        const hasActions =
          usecaseContent.actions &&
          Object.keys(usecaseContent.actions).length > 0;
        if (hasActions) {
          actions = Object.entries(usecaseContent.actions).map(
            ([actionID, actionDone]) => ({ id: actionID, done: actionDone }),
          );
        }

        const initActionButton = (actionID) => {
          const onClickFn = {
            add_representant_email: () => navigate(routes.Company.reps),
            synchronise_accounts: () => navigate(routes.Vault.bank),
            import_statement: () => navigate(routes.Vault.finance),
            fill_banking_data_6: () => navigate(routes.Vault.bank),
            fill_banking_data_12: () => navigate(routes.Vault.bank),
            import_bank_statements: () => navigate(routes.Vault.bank),
          };

          const handleActionWithHelper = (actionID) => {
            const actionHelper = {
              heading: t("financing:unlock.actions.helper.heading"),
              subheading: t("financing:unlock.actions.helper.subheading"),
              button: t("financing:unlock.actions.helper.button"),
              navigate: {
                target: routes.Financing.unlock,
                state: {
                  need: need,
                },
              },
            };

            onClickFn[actionID]();
            dispatchHelper({
              type: "add",
              expand: true,
              ...actionHelper,
            });
          };

          return {
            onClick: () => handleActionWithHelper(actionID),
            text: t(`financing:unlock.actions.ids.${actionID}.button`),
          };
        };

        const initActions = (
          actions = [
            { id: "actionID1", done: true },
            { id: "actionID2", done: false },
          ],
        ) =>
          actions.map((action) => ({
            id: action.id,
            icon: <FileCopy />,
            title: t(`financing:unlock.actions.ids.${action.id}.title`),
            subtitle: t(`financing:unlock.actions.ids.${action.id}.subtitle`),
            button: action.done ? null : initActionButton(action.id),
            buttonAlt: action.done,
          }));

        // ---- Use cases ---- //

        const initUsecaseElements = () => {
          const pill = (color, text) => ({
            color: color,
            text: text,
          });

          const moreInfoButton = () => {
            const moreInfoModalNote = reasons && (
              <ul style={{ padding: "revert", margin: "0" }}>
                {reasons.map((reasonID, index) => (
                  <li style={{ listStyle: "revert" }} key={index}>
                    {capitalize(
                      t(
                        `financing:unlock.usecases.modals.moreInfo.note.reasons.${reasonID}`,
                      ),
                    )}
                  </li>
                ))}
              </ul>
            );

            const moreInfoModal = {
              title: t("financing:unlock.usecases.modals.moreInfo.title"),
              message: t("financing:unlock.usecases.modals.moreInfo.message"),
              note: moreInfoModalNote,
              size: "medium",
            };

            return {
              text: t("financing:unlock:usecases.buttons.moreInfo"),
              onClick: () =>
                dispatchModal({
                  type: "add",
                  ...moreInfoModal,
                }),
            };
          };

          const viewActionsButton = () => ({
            text: t("financing:unlock.usecases.buttons.unlock"),
            onClick: () => setSelectedUsecase(usecaseID),
          });

          const requestFinancingButton = () => {
            let button = {
              text: t("financing:unlock.usecases.buttons.request"),
            };

            const initRedirectModal = (provider, redirectURL) => {
              return {
                title: t("financing:unlock.usecases.modals.redirect.title"),
                message: (
                  <Trans
                    t={t}
                    i18nKey={
                      "financing:unlock.usecases.modals.redirect.message"
                    }
                    values={{ provider }}
                  />
                ),
                component: (
                  <Form
                    button={t(
                      "financing:unlock.usecases.modals.redirect.button",
                    )}
                    onSubmit={() => (window.location.href = redirectURL)}
                  />
                ),
              };
            };

            const handleGCollect = () => {
              const redirectURL =
                "https://pro.gcollect.fr/inscription/c/trouver-votre-entreprise?affiliation_code=LEA-0-100";
              const note1URL = "https://www.gcollect.fr/9155-2/";

              const note = (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                  }}
                >
                  <p>
                    <Trans
                      t={t}
                      i18nKey={
                        "financing:unlock.usecases.modals.debt_collection.GCollectNotes.note1"
                      }
                      components={{ anchor: <a href={note1URL} /> }}
                    />
                  </p>
                  <p>
                    {t(
                      "financing:unlock.usecases.modals.debt_collection.GCollectNotes.note2",
                    )}
                  </p>
                </div>
              );

              const modal = {
                ...initRedirectModal("GCollect", redirectURL),
                note,
              };

              dispatchModal({ type: "add", ...modal });
            };

            const handleDefacto = () => {
              const redirectURL =
                "https://app.getdefacto.com/onboarding?referral=DEFACTO-LEANO-REFERRAL";

              const modal = {
                ...initRedirectModal("Defacto", redirectURL),
              };

              dispatchModal({ type: "add", ...modal });
            };

            switch (usecaseID) {
              case "debt_collection":
                button = {
                  ...button,
                  text: t("financing:unlock.usecases.buttons.collect"),
                  onClick: handleGCollect,
                };
                break;
              case "short_term_invoice":
                button = {
                  ...button,
                  text: t("financing:unlock.usecases.buttons.request"),
                  onClick: handleDefacto,
                };
                break;

              default:
                button = {
                  ...button,
                  onClick: () => goForward(usecaseID),
                };
            }

            return button;
          };

          const registerButton = () => {
            const links = [
              {
                urls: {
                  en: "https://cdn.prod.website-files.com/65186d27d0dcf0d8e7a48388/669f9ac1f9f5f07cc3d578f5_Terms%20of%20service%20-%20EN%20-%20202407.docx.pdf",
                  fr: "https://cdn.prod.website-files.com/65186d27d0dcf0d8e7a48388/669f9ac1f9f5f07cc3d578f5_Terms%20of%20service%20-%20EN%20-%20202407.docx.pdf",
                  it: "https://cdn.prod.website-files.com/65186d27d0dcf0d8e7a48388/669f9ac1f9f5f07cc3d578f5_Terms%20of%20service%20-%20EN%20-%20202407.docx.pdf",
                },
                provider: "Defacto",
              },
              {
                urls: {
                  en: "https://www.airpme.com/fr/mentions-legales.html",
                  fr: "https://www.airpme.com/fr/mentions-legales.html",
                  it: "https://www.airpme.com/fr/mentions-legales.html",
                },
                provider: "AirPME",
              },
            ];

            const anchor = (provider) => (
              <a
                href={
                  links.find((link) => link.provider === provider).urls[
                    i18n.resolvedLanguage
                  ]
                }
              >
                {provider}
              </a>
            );

            const message = () => {
              const many = providers.length > 1;
              return (
                <Trans
                  t={t}
                  i18nKey={`financing:unlock.usecases.modals.register.message_${
                    many ? "many" : "one"
                  }`}
                  values={!many ? { provider: providers[0] } : {}}
                  components={
                    !many
                      ? {
                          anchor: anchor(providers[0]),
                        }
                      : {}
                  }
                />
              );
            };

            const providersLinksNote = (
              <ul style={{ padding: "revert", margin: "0" }}>
                {providers.map((provider, index) => (
                  <li style={{ listStyle: "revert" }} key={index}>
                    {anchor(provider)}
                  </li>
                ))}
              </ul>
            );

            const registerModal = {
              title: t("financing:unlock.usecases.modals.register.title"),
              message: message(),
              component: <Registration need={need} providers={providers} />,
              note: providers.length > 1 ? providersLinksNote : null,
              onSuccess: init,
            };

            return {
              text: t("financing:unlock.usecases.buttons.register"),
              onClick: () => dispatchModal({ type: "add", ...registerModal }),
            };
          };

          const pendingButton = () => {
            const pendingModal = {
              title: t("financing:unlock.usecases.modals.pending.title"),
              message: t("financing:unlock.usecases.modals.pending.message"),
              size: "medium",
            };

            return {
              text: t("financing:unlock:usecases.buttons.pending"),
              onClick: () =>
                dispatchModal({
                  type: "add",
                  ...pendingModal,
                }),
            };
          };

          const variants = {
            unavailable: {
              pill: pill("red", capitalize(t("common:unavailable"))),
              button: moreInfoButton(),
              component: creditLine && (
                <CreditLine
                  available={creditLine.available_amount}
                  max={creditLine.max_amount}
                  currency={activeCompany.currency}
                  date={creditLine.date}
                />
              ),
            },
            available: {
              pill: pill("yellow", capitalize(t("common:toUnlock"))),
              button: viewActionsButton(),
              component: creditLine && (
                <CreditLine
                  stale
                  available={creditLine.available_amount}
                  max={creditLine.max_amount}
                  currency={activeCompany.currency}
                  date={creditLine.date}
                />
              ),
            },
            register: {
              pill: pill("yellow", capitalize(t("common:toUnlock"))),
              button: providers && registerButton(),
            },
            pending: {
              pill: pill("yellow", capitalize(t("common:toUnlock"))), // create "en attente" ?
              button: pendingButton(),
            },
            active: {
              pill: pill("green", capitalize(t("common:available"))),
              button: requestFinancingButton(),
              component: creditLine && (
                <CreditLine
                  available={creditLine.available_amount}
                  max={creditLine.max_amount}
                  currency={activeCompany.currency}
                  date={creditLine.date}
                />
              ),
            },
          };

          return variants[status];
        };

        return {
          id: usecaseID,
          icon: <Grading />,
          title: t(`financing:unlock.usecases.ids.${usecaseID}.title`),
          subtitle: t(`financing:unlock.usecases.ids.${usecaseID}.subtitle`),
          ...initUsecaseElements(),
          actions: initActions(actions),
        };
      },
    );

    setUsecases(newUsecases);
  };

  const init = async () => {
    const response = await api.getUsecases(need);
    if (response.ok) {
      const jsonUsecases = await response.json();
      const testUsecases = false;
      testUsecases ? initUsecases() : initUsecases(jsonUsecases);
    }
    setLoading(false);
  };

  useEffect(() => {
    init();
  }, []);

  if (!selectedUsecase)
    return (
      <Usecases
        need={need}
        usecases={usecases}
        onBack={goBack}
        loading={loading || loadingActiveCompany}
        results={results}
      />
    );

  const useCase = usecases.filter(
    (usecase) => usecase.id === selectedUsecase,
  )[0];
  return (
    <Actions
      id={useCase.id}
      actions={useCase.actions}
      onBack={() => setSelectedUsecase("")}
      loading={loading || loadingActiveCompany}
    />
  );
}

function Usecases({ need, usecases, onBack, loading, results }) {
  const { t } = useTranslation(["common", "financing"]);

  const [drawer, setDrawer] = useState(false);
  const toggleDrawer = () => setDrawer((drawer) => !drawer);

  return (
    usecases && (
      <Main themeSwitch>
        <Header
          onBackButtonClick={onBack}
          heading={t(`financing:assessment.results.keys.${need}.name`)}
          subheading={
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
              }}
            >
              <span>{t("financing:unlock.usecases.subheading")}</span>
              <a
                style={{ textDecoration: "underline", cursor: "pointer" }}
                onClick={toggleDrawer}
              >
                {t("financing:unlock.usecases.subheading_link")}
              </a>
            </div>
          }
        />
        <Content>
          {loading ? <FormLoader /> : <Cards cards={usecases} />}
          <PartnersDrawer
            result={need && Object.entries(results[need])}
            open={drawer}
            onClose={toggleDrawer}
          />
        </Content>
      </Main>
    )
  );
}

function Actions({ id, actions, onBack, loading }) {
  const { t } = useTranslation(["common", "financing"]);

  return (
    actions && (
      <Main themeSwitch>
        <Header>
          <Heading onBackButtonClick={onBack}>
            {t(`financing:unlock.usecases.ids.${id}.title`)}
          </Heading>
          <SubHeading>{t("financing:unlock.actions.subheading")}</SubHeading>
        </Header>
        <Content>
          {loading ? (
            <FormLoader />
          ) : (
            <div className="Actions">
              <div className="Actions-header">
                <div className="Actions-header-text">
                  <div className="Actions-header-text-heading">
                    {t("financing:unlock.actions.counter.heading")}
                  </div>
                  <div className="Actions-header-text-subheading">
                    {t("financing:unlock.actions.counter.subheading")}
                  </div>
                </div>
                <div className="Actions-header-progress">
                  <ProgressBar
                    status
                    length={actions.length}
                    index={actions.filter((action) => !action.button).length}
                  />
                </div>
              </div>
              <Cards cards={actions} />
            </div>
          )}
        </Content>
      </Main>
    )
  );
}

function Cards({ cards }) {
  return (
    <div className="Cards">
      {cards.map((card) => (
        <Card key={card.id} {...card} />
      ))}
    </div>
  );
}

function Card({
  icon,
  title,
  pill,
  subtitle,
  button,
  buttonAlt = false,
  component,
}) {
  return (
    <div className="Card">
      <div className="Card-icon">{icon}</div>

      <div className="Card-container">
        <div className="Card-container-description">
          <div className="Card-container-description-title">
            <div className="Card-container-description-title-text">{title}</div>
            <div className="Card-container-description-title-pill">
              {pill && <Pill color={pill.color} text={pill.text} />}
            </div>
          </div>
          <p className="Card-container-description-subtitle">{subtitle}</p>
          {component && (
            <div className="Card-container-description-component">
              {component}
            </div>
          )}
        </div>
        <div className="Card-container-cta">
          {button ? (
            <Button
              className="Card-container-cta-button"
              variant={"primary_a"}
              text={button.text}
              onClick={button.onClick}
              icon={button.icon}
              iconLeft={button.iconLeft}
              disabled={button.disabled}
            />
          ) : buttonAlt ? (
            <CheckCircleRounded className="Card-container-cta-check" />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Registration({ need, providers }) {
  const { t } = useTranslation(["common", "financing"]);
  const dispatchModal = useModalDispatch();
  const dispatchToast = useToastsDispatch();

  const initialErrors = {
    amount: [],
    non_field_errors: [],
  };

  const [amount, setAmount] = useState("");
  const [errors, setErrors] = useState(initialErrors);

  const clearErrors = () => {
    setErrors(initialErrors);
  };

  const amountInput = {
    type: "number",
    label: "Montant",
    value: amount,
    onChange: setAmount,
    min: 0,
    max: 75000,
    errors: errors.amount,
  };

  const isAmountRequired = () =>
    providers.some((provider) => provider === "AirPME");

  const inputs = [
    {
      data: [amountInput],
    },
  ];

  const handleRegistration = async () => {
    clearErrors();
    // 1 - Register each provider
    let responseError = null;
    for (const provider of providers) {
      const response = await api.registerBorrower(
        need,
        provider,
        isAmountRequired() ? { amount: amount } : null,
      );
      if (!response.ok) {
        const jsonResponse = await response.json();
        responseError = response.status;
        switch (responseError) {
          case 502:
            dispatchModal({ type: "success" });
            dispatchToast({
              type: "add",
              variant: "info",
              subheading: t(
                "financing:unlock.usecases.modals.register.toasts.partner_busy",
              ),
            });
            break;

          case 400:
            // Errors shown in toast format
            if (Object.keys(jsonResponse).includes("errors")) {
              dispatchModal({ type: "success" });
              for (const error of jsonResponse.errors) {
                const knownErrors = ["IBAN_invalid", "representants_missing"];
                if (knownErrors.some((knownError) => knownError === error)) {
                  dispatchToast({
                    type: "add",
                    variant: "error",
                    heading: t(
                      "financing:unlock.usecases.modals.register.toasts.heading.error",
                    ),
                    subheading: t(
                      `financing:unlock.usecases.modals.register.toasts.${error}`,
                    ),
                  });
                } else {
                  const field = error.split("_")[0];
                  const reason = error.split("_")[1];

                  dispatchToast({
                    type: "add",
                    variant: "error",
                    subheading: (
                      <Trans
                        t={t}
                        i18nKey={
                          "financing:unlock.usecases.modals.register.toasts.field_reason"
                        }
                        values={{ field: field, reason: reason }}
                      />
                    ),
                  });
                }
              }
            }
            // Form fields validation
            else {
              setErrors(jsonResponse);
              break;
            }

            break;
          default:
            dispatchModal({ type: "success" });
            console.log(responseError);
            dispatchToast({
              type: "add",
              variant: "info",
              subheading: t("common:toasts.errorToast.subheading"),
            });
        }
      }
    }

    // 2 - If registration OK: close modal and dispatch success toast
    if (!responseError) {
      dispatchModal({ type: "success" });
      dispatchToast({
        type: "add",
        variant: "success",
        subheading: t(
          `financing:unlock.usecases.modals.register.toasts.${"success"}`,
        ),
      });
    }
  };

  return (
    <Form
      inputs={isAmountRequired() ? inputs : []}
      errors={errors.non_field_errors}
      onSubmit={handleRegistration}
      button={t("financing:unlock.usecases.modals.register.component.button")}
    />
  );
}
